export type Notification = {
  _id: string;
  createdAt: Date;
  updatedAt: Date;
  userId: string;
  platforms: NotificationPlatform[];
  type: NotificationName;
  seen: boolean;
  values?: Record<string, string | number>;
  emailMessageId?: string;
  emailOpenedAt?: Date;
  emailStatus?: EmailStatus;
};

export const NotificationPlatform = {
  APP: 'app',
  EMAIL: 'email',
} as const;

export type NotificationPlatform =
  (typeof NotificationPlatform)[keyof typeof NotificationPlatform];

export const NotificationPlatforms = Object.values(NotificationPlatform);

export const NotificationName = {
  Termination: 'termination',
  EvaluationAboutToExpire: 'about-to-expire',
  DemoAboutToExpire: 'demo-is-about-to-expired',
  ReachedExpiration: 'reached-expiration',
  ReachedDailyPause: 'daily-pause-reached',
  ReachedDailyLoss: 'daily-loss-reached',
  AfterPurchase: 'after-purchasing',
  ResetPassword: 'reset-password',
  Welcome: 'welcome',
  UserVerificationRequired: 'user-verification-required',
  UserVerificationCompleted: 'user-verification-completed',
  UserVerificationFailed: 'user-verification-failed',
  EmailConfirmation: 'confirmation-email',
  NewAccount: 'new-account',
  AddOnApplied: 'add-on-applied',
  ReachedTargetEvaluationToEvaluation: 'eval-to-eval-passed',
  ReachedTargetToFunded: 'email-15-reached-target-account-passed',
  ResetDailyPause: 'email-13-account-renewed-after-daily-pause',
  FundedAccountReady: 'email-17-funded-account-is-ready',
  FundedAccountReadyInterview: 'funded-account-ready-2-interview',
  RequestPayout: 'email-payout-request-confirmation',
  GotPaid: 'email-16-you-got-paid',
  InterviewInvitation: '26-interview',
  ReachedInactivityDays: 'reached-inactivity-days',
  WarnReachingInactivityDays: 'warn-reaching-inactivity-days',
  AdminAccountPayout: 'admin-account-payout',
  AdminAffiliatePayout: 'admin-affiliate-payout',
  AdminPayoutRequest: 'admin-payout-request',
  NewTradeTalkPublished: 'new-trade-talk-published',
  TradeTalkCoolTime: 'trade-talk-cool-time',
  WithdrawalRequest: 'email-18-withdrawal-request',
  WithdrawalAffiliateRequest: 'affiliate-withdraw-verification',
  Contest: 'contest',
  ArticleRejected: 'article-rejected',
  ArticleUnderReview: 'article-in-review',
  ArticleApproved: 'article-approved',
  ContestRegistration: 'contest-registration',
  ContestPrize: 'contest-prize',
  ContestStarted: 'contest-started',
  ContestLastDay: 'contest-last-day',
  ContestEnded: 'contest-ended',
  MaxLossTargetReached: 'max-loss-target-reached',
  PreFundedProcess: 'email-10---pre-funded-process-1',
  TalkCommentReceived: 'talk-comment-received',
  TalkCommentReceivedGrouped: 'talk-comment-received-grouped',
  TalkLikeReceived: 'talk-like-received',
  TalkLikeReceivedGrouped: 'talk-like-received-grouped',
  TalkEditorPickReceived: 'talk-editor-pick-received',
  TradingEndWarning: 'futures-hold-positions-overnight-reminder',
  WeekendWarning: 'over-the-weekend-futures',
  CreditExpirationWithin2Days: 'credit-expiration-within-2-days',
  CreditExpirationWithin7Days: 'credit-expiration-within-7-days',
  HubCreditReceived: 'hub-credit-received',
  WithdrawalSocialMediaPromotionCoupon: '26-first-time-approval',
  WithdrawalSocialMediaPromotionCredits: '26-second-time-approval',
} as const;

export type NotificationName =
  (typeof NotificationName)[keyof typeof NotificationName];

export const NotificationNames = Object.values(NotificationName);

export const EmailStatus = {
  SENT: 'sent',
  OPENED: 'opened',
  BOUNCED: 'bounced',
  SPAM: 'spam',
  FAILED: 'failed',
} as const;

export type EmailStatus = (typeof EmailStatus)[keyof typeof EmailStatus];

export const EmailStatuses = Object.values(EmailStatus);

/* APP - Response DTO for frontend consumption */
export type NotificationResponse = {
  id: string;
  type: NotificationName;
  seen: boolean;
  values: Record<string, string | number>;
  createdAt: string;
};

/* APP */
export type CreateNotificationPayload = Pick<
  Notification,
  'userId' | 'type' | 'values'
>;

export type CreateNotificationBody = CreateNotificationPayload & {
  platforms: NotificationPlatform[];
};

/* EMAIL */
export type CreateNotificationEmailPayload = CreateNotificationPayload & {
  emailMessageId: string;
  emailOpenedAt: Date;
  emailStatus: EmailStatus;
};

export type CreateNotificationEmailBody = CreateNotificationEmailPayload & {
  platforms: NotificationPlatform[];
};

/* APP & EMAIL */
export type CreateNotificationEmailAndAppPayload = CreateNotificationPayload &
  CreateNotificationEmailPayload;

export type CreateNotificationEmailAndAppBody = CreateNotificationBody &
  CreateNotificationEmailBody & {
    platforms: NotificationPlatform[];
  };
